import React from 'react';
import type { NPC, Quest } from '../../../../shared/NPCData';
import { resolveContextualQuestAction } from '../../../../shared/logic/contextualQuestAction';
import { useInputModality } from '../../hooks/useInputModality';
import { InputHint } from '../../primitives/InputHint';
import { QuestCard } from './QuestCard';
import { QuestCodexPage } from './codex/QuestCodexPage';
import { TomeChapter } from '../../primitives/tome';
import { usePlayerWhereabouts } from './guidance/playerWhereabouts';
import type { TurnInBlock } from './turnInBlock';
import { questViewOrder } from './questViewOrder';
import './NPCPlayerUI.css';

interface QuestViewProps {
  npc: NPC;
  playerLevel: number;
  npcTypeInfo: { color: string; icon: string; name: string };
  isQuestAvailable: (quest: Quest) => boolean;
  isQuestCompletable: (quest: Quest) => boolean;
  onQuestAccept?: (quest: Quest) => void;
  onQuestComplete?: (quest: Quest) => void;
  /** Explains a ready turn-in that won't fit the bag (plan 06 §2.4). */
  turnInBlockFor?: (quest: Quest) => TurnInBlock | null;
  /** "No quests here." when the page is empty (off under a completion notice). */
  showEmpty?: boolean;
}

/**
 * The NPC's quests in one order (plan 13 §2): everything ready to turn in
 * comes first — featured folios and ordinary cards alike, each with its Turn
 * In button above the fold — then featured offers, locked teasers, available,
 * in progress and completed. With a folio offer on the page, ordinary offers
 * fold their story so the folio leads. Eligibility, objective progress and
 * the contextual controller target retain their authority; grouping only
 * orders them. `questViewOrder` is the rule; the render follows it.
 */
export const QuestView: React.FC<QuestViewProps> = ({
  npc, playerLevel, isQuestAvailable, isQuestCompletable, onQuestAccept, onQuestComplete, turnInBlockFor,
  showEmpty = true,
}) => {
  const { modality, controller } = useInputModality();
  const you = usePlayerWhereabouts();
  const questBinding = controller?.bindings.questAction;
  const contextual = resolveContextualQuestAction(npc.quests, playerLevel);
  const hint = (quest: Quest) => modality === 'gamepad' && questBinding?.label && contextual?.questId === quest.id
    ? <InputHint label={questBinding.label} device="gamepad" title={`${questBinding.label} controller shortcut`}
      testId={`quest-controller-hint-${quest.id}`} /> : null;
  const actionsFor = (quest: Quest) => ({
    canAccept: playerLevel >= quest.requiredLevel && isQuestAvailable(quest),
    canComplete: isQuestCompletable(quest),
    onQuestAccept, onQuestComplete, hint: hint(quest), blocked: turnInBlockFor?.(quest) ?? null,
  });

  const order = questViewOrder(npc);
  const empty = order.length === 0;
  return <div className="npc-quests">
    {empty && showEmpty && <p className="npc-empty" role="status">No quests here.</p>}
    {order.map(slot => slot.kind === 'folio'
      ? <QuestCodexPage key={slot.quest.id} quest={slot.quest} mode="featured" playerLevel={playerLevel}
        you={you} actions={actionsFor(slot.quest)} />
      : slot.kind === 'teaser'
        ? <QuestCodexPage key={slot.quest.id} quest={slot.quest} mode="teaser" playerLevel={playerLevel} />
        : <TomeChapter key={slot.group.id} className="npc-quest-group" data-quest-group={slot.group.id}
          title={slot.group.title} count={slot.group.quests.length} tone={slot.group.tone}>
          {slot.group.quests.map(quest => <QuestCard key={quest.id} quest={quest} variant={slot.group.variant} you={you}
            playerLevel={playerLevel} {...actionsFor(quest)} />)}
        </TomeChapter>)}
  </div>;
};
